import React, { useCallback, useEffect, useState } from 'react';
import { Search, Flag, FlagOff, Trash2, ChevronLeft, ChevronRight, Loader2, Star } from 'lucide-react';
import dayjs from 'dayjs';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { usePermissions } from '../../admin/usePermissions';
import { toast } from 'sonner';

export default function AdminReviewsPage() {
  const { has } = usePermissions();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFlagged, setIsFlagged] = useState('all');
  const [eventId, setEventId] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const params = { limit, offset };
      if (isFlagged === 'yes') params.is_flagged = true;
      if (isFlagged === 'no') params.is_flagged = false;
      if (eventId.trim()) params.event_id = eventId.trim();
      const r = await adminApi.get('/reviews', { params });
      setItems(r.data?.items || r.data || []);
      setTotal(r.data?.total ?? (r.data?.length || 0));
    } catch (e) { toast.error(adminErrorMsg(e)); }
  }, [isFlagged, eventId, offset]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const flagReview = async (id) => {
    try { await adminApi.post(`/reviews/${id}/flag`); toast.success('Review flagged'); await load(); }
    catch (e) { toast.error(adminErrorMsg(e)); }
  };

  const unflagReview = async (id) => {
    try { await adminApi.post(`/reviews/${id}/unflag`); toast.success('Review unflagged'); await load(); }
    catch (e) { toast.error(adminErrorMsg(e)); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try { await adminApi.delete(`/reviews/${id}`); toast.success('Review deleted'); await load(); }
    catch (e) { toast.error(adminErrorMsg(e)); }
  };

  const pages = Math.ceil(total / limit);
  const page = Math.floor(offset / limit) + 1;

  return (
    <div data-testid="admin-reviews-page">
      <h1 className="text-xl font-black text-gray-900 mb-4">Reviews</h1>

      <div className="flex flex-wrap gap-2 mb-4">
        <select data-testid="reviews-flag-filter" value={isFlagged} onChange={(e) => { setIsFlagged(e.target.value); setOffset(0); }} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700">
          <option value="all">All Reviews</option>
          <option value="yes">Flagged Only</option>
          <option value="no">Not Flagged</option>
        </select>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3">
          <Search className="w-4 h-4 text-gray-400" />
          <input data-testid="reviews-event-filter" value={eventId} onChange={(e) => { setEventId(e.target.value); setOffset(0); }} placeholder="Filter by event ID..." className="py-2 text-sm bg-transparent focus:outline-none w-[200px]" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-magenta" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reviews-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Rating</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Body</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Flagged</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="text-right px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((rv) => (
                  <tr key={rv.id} data-testid={`review-row-${rv.id}`} className={`hover:bg-gray-50 transition-colors ${rv.is_flagged ? 'bg-red-50/30' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map((i) => (
                          <Star key={i} className={`w-3 h-3 ${i <= rv.rating ? 'text-brand-magenta fill-[#E91E63]' : 'text-gray-300'}`} />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900 max-w-[140px] truncate">{rv.title || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{rv.body}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{rv.user_name || rv.user_id?.slice(0, 8)}</td>
                    <td className="px-4 py-3">
                      {rv.is_flagged ? (
                        <span className="bg-red-50 text-red-600 text-[10px] font-black px-2 py-0.5 rounded-full">FLAGGED</span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{dayjs(rv.created_at).format('MMM D, YY')}</td>
                    <td className="px-4 py-3 text-right flex items-center justify-end gap-1">
                      {rv.is_flagged ? (
                        <button data-testid={`unflag-${rv.id}`} onClick={() => unflagReview(rv.id)} className="p-1.5 rounded hover:bg-emerald-50" title="Unflag">
                          <FlagOff className="w-4 h-4 text-emerald-600" />
                        </button>
                      ) : (
                        <button data-testid={`flag-${rv.id}`} onClick={() => flagReview(rv.id)} className="p-1.5 rounded hover:bg-amber-50" title="Flag">
                          <Flag className="w-4 h-4 text-amber-600" />
                        </button>
                      )}
                      {has('reviews.remove') && (
                      <button data-testid={`delete-review-${rv.id}`} onClick={() => deleteReview(rv.id)} className="p-1.5 rounded hover:bg-red-50" title="Delete">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {items.length === 0 && <p className="text-center text-sm text-gray-400 py-10">No reviews found</p>}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">{total} total · Page {page}/{pages}</p>
          <div className="flex gap-1">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
            <button disabled={page >= pages} onClick={() => setOffset(offset + limit)} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
